import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import {
  Button,
  Dropdown,
  DropdownItem,
  InlineInfoBox,
  TextFieldSts,
} from "staysure-component-library";

import TransformAnimation from "@/components/atoms/animation/transform-animation";
import RichText from "@/components/atoms/rich-text/rich-text";
import withBrand from "@/components/with-brand";
import {
  useAddressApi,
  useFetchAddressData,
} from "@/hooks/api/address-api/address-api";
import useDebounce from "@/hooks/debounce/debounce";
import useMediaQuery from "@/hooks/use-media-query/use-media-query";
import { formatDate as canonicalFormatDate } from "@/libs/i18n/format-date";
import { AddressType } from "@/types/AddressTypes";
import { OrganiserEditFormProps } from "@/types/OrganiserType";
import { showMessenger } from "@/utils/cxone.utils";
import {
  checkValidation,
  getStrokeColor,
  ifAddressData,
  inputCharacterCount,
  regexValidaton,
} from "@/utils/organiser.utils";
import { useTranslatedTitleData } from "@/utils/traveller.utils";

import Styles from "../../questions-container/organizer/organiser-answer/organiser-form.module.css";

function OrganizerEditForm({
  addressLineOne,
  addressLineTwo,
  brand,
  dobPlaceholder,
  enterAddressButton,
  inputOnePlaceholder,
  inputTwoPlaceholder,
  organizerDetails,
  postcodePlaceholder,
  renewalInfoText,
  searchByPostCodeButton,
  townCityPlaceholder,
  handleUpdatedData,
  ...props
}: Readonly<OrganiserEditFormProps>) {
  const isDesktop = useMediaQuery("(min-width: 600px)");
  const titleData = useTranslatedTitleData();

  const [isTitleFocused, setIsTitleFocused] = useState<boolean>(false);
  const [isTabPressed, setIsTabPressed] = useState(false);

  const [formData, setFormData] = useState(organizerDetails);

  const [addressID, setAddressID] = useState<string>("");
  const [suggestions, setSuggestions] = useState<AddressType[]>([]);

  const [postCode, setPostCode] = useState<string | null>("");

  const [addressErrors, setAddressErrors] = useState<string>("");
  const [valueAddress, setValueAddress] = useState<string>("");

  const [showPostcodeSearch, setShowPostcodeSearch] = useState(false);
  const [invalidErrorMessage, setInvalidErrorMessage] = useState<
    Record<string, string>
  >({});

  const debouncedSearchTerm = useDebounce(valueAddress, 500);
  const [address, fetchAddress] = useAddressApi(debouncedSearchTerm, null);

  const [addressDataFetch, fetchAddressDetails] =
    useFetchAddressData(addressID);
  const titleDropdownRef = useRef<HTMLDivElement>(null);

  const validateInput = (fieldName: string, value: string): boolean => {
    // Skip validation if fieldName is 'line2' and value is empty
    if (fieldName === "line2" && value.trim() === "") {
      setInvalidErrorMessage((prev) => {
        const { [fieldName]: _ignored, ...rest } = prev;
        return rest; // Remove the field from the error state if it exists
      });
      return true;
    }

    const validations = checkValidation(fieldName, "UK", props);

    const errorMessages = validations
      .map((validation) => {
        if (validation.errorType === "regex") {
          return regexValidaton(value, validation.validation as RegExp)
            ? validation.errorMsg
            : null;
        }
        if (validation.errorType === "maxTextCount") {
          return inputCharacterCount(value, validation.validation as number)
            ? validation.errorMsg
            : null;
        }
        return null;
      })
      .filter((error) => error !== null); // Remove null (valid results)

    if (errorMessages.length > 0) {
      setInvalidErrorMessage((prev) => ({
        ...prev,
        [fieldName]: errorMessages[0], // Use the first error message
      }));
      return false; // Validation failed
    }

    setInvalidErrorMessage((prev) => {
      const { [fieldName]: _ignored, ...rest } = prev;
      return rest; // Remove the field from the error state
    });
    return true; // Validation passed
  };

  useEffect(() => {
    const isCompleted = Object.keys(invalidErrorMessage).length === 0;
    handleUpdatedData(formData, isCompleted);
  }, [formData, handleUpdatedData, invalidErrorMessage]);

  useEffect(() => {
    if (valueAddress) {
      fetchAddress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  useEffect(() => {
    setSuggestions(address);
    if (address?.length) {
      setAddressErrors("");
    } else if (valueAddress?.length) {
      setAddressErrors("Please enter a valid postcode");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  useEffect(() => {
    if (addressID) {
      fetchAddressDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressID]);

  useEffect(() => {
    if (addressDataFetch?.[0]) {
      if (ifAddressData(addressDataFetch)) {
        const { attributes } = addressDataFetch[0];

        setFormData((prev) => ({
          ...prev,
          firstLineOfAddress: attributes.line1.trim() || "",
          secondLineOfAddress: attributes.line2.trim() || "",
          city: attributes.city.trim() || "",
          postCode: attributes.postalCode.trim() || "",
          country: "GB",
        }));
        setPostCode(attributes.postalCode.trim() || "");
      }
    }
  }, [addressDataFetch]);

  useEffect(() => {
    function handleKeyDown(event: { key: string }) {
      if (event.key === "Tab") {
        setIsTabPressed(true);
      }
    }

    function handleKeyUp(event: { key: string }) {
      if (event.key === "Tab") {
        setIsTabPressed(false);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  const handleInputChange = (fieldName: string, value: string | boolean) => {
    // Mapping for fieldName
    const fieldNameMapping: Record<string, string> = {
      line1: "firstLineOfAddress",
      line2: "secondLineOfAddress",
      postalCode: "postCode",
    };

    // Determine the correct field name using the mapping
    const mappedFieldName = fieldNameMapping[fieldName] || fieldName;

    if (typeof value === "string" && !validateInput(fieldName, value)) {
      return;
    }
    setFormData((prev) => ({ ...prev, [mappedFieldName]: value }));
  };

  const handleAddressSelection = (selectedAddress: AddressType) => {
    setAddressID(selectedAddress.id);
    setSuggestions([]);
    setShowPostcodeSearch(false);
  };

  const scrollInputToTop = () => {
    const inputField = document.getElementById("scrollableInput");
    if (inputField) {
      inputField.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const fontConfig = [
    {
      brand: "staysure",
      fontColorClass: "text-cyan-800",
    },
    {
      brand: "avanti",
      fontColorClass: "text-interactive-secondary-default",
    },
  ];

  function getTextColorClasses(lBrand: string | undefined): string {
    let fontClasses = "";
    fontConfig.forEach((config) => {
      if (config.brand === lBrand) {
        fontClasses = `${config.fontColorClass}`;
      }
    });
    return fontClasses;
  }

  function formatDate(dateString: string | undefined) {
    return canonicalFormatDate(dateString, "dd/MM/yyyy");
  }

  const calculateAge = () => {
    return dayjs().diff(dayjs(formData.dateOfBirth), "year");
  };

  const handleWebChatClick = () => {
    showMessenger();
  };

  return (
    <div className="flex flex-col">
      <div className=" flex flex-col gap-x-2 md:mt-spacing-l mt-spacing-m">
        <div
          data-testid="organiser-form"
          className={`${Styles.trvellers_form} md:bg-surface-neutral-option-1 bg-surface-neutral-option-2`}
        >
          <div className="field-block md:!pb-spacing-xl !px-0">
            <div
              ref={titleDropdownRef}
              className={` ${Styles.country_wrapper} ${Styles.title_dropdown} ${
                isTitleFocused && Styles.dropdown_focused
              } flex flex-row items-start  max-w-[106px] w-full h-[56px] mb-spacing-s`}
              onFocus={() => {
                if (isTabPressed) {
                  setIsTitleFocused(true);
                }
                setTimeout(() => {
                  titleDropdownRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                }, 300);
              }}
              onBlur={() => setIsTitleFocused(false)}
            >
              <Dropdown
                id="personal-status-dropdown"
                defaultValue={formData.title}
                strokeColor={getStrokeColor(brand)}
                onSelectionChange={(value: string) =>
                  handleInputChange("title", value as string)
                }
                className={`${Styles.droplistBox}  border-interactive-secondary-default rounded-radius-lg 
                  max-h-spacing-xxxl`}
                listBoxStyle={{
                  outline: "none",
                  margin: "0px 0px 0px 0px",
                  minWidth: "unset !important",
                  boxShadow: "0px 0px 12px 0px rgba(15, 23, 42, 0.11)",
                }}
                listClassName={` ${Styles.listStyle} mt-[10px] rounded-radius-s`}
                unorderedListClassName="w-[100px] !mt-[3px] !md:ml-0 !ml-[-1px] !border-border-subdued !rounded-radius-lg 
               !px-spacing-m !py-spacing-xs"
              >
                {titleData.map((title) => (
                  <DropdownItem key={title.id} value={title.displayName}>
                    <span
                      className=" w-full leading-[24px] font-proximanova text-2xs font-semibold p-spacing-xs 
                     country-name "
                    >
                      {title.displayName}
                    </span>
                  </DropdownItem>
                ))}
              </Dropdown>
            </div>

            <div
              className={`${Styles.input_wrapper} flex flex-row items-start mb-spacing-s`}
            >
              <TextFieldSts
                isDisabled
                label={inputOnePlaceholder ?? ""}
                data-testid="first-name-organiser"
                id="first-name-organiser"
                aria-label="first-name-organiser"
                width="100%"
                name="firstName"
                value={formData.firstName}
                brand={brand}
              />
            </div>

            <div
              className={`${Styles.input_wrapper} flex flex-row items-start mb-spacing-s`}
            >
              <TextFieldSts
                isDisabled
                label={inputTwoPlaceholder ?? ""}
                data-testid="last-name-organiser"
                id="last-name-organiser"
                aria-label="last-name-organiser"
                width="100%"
                name="lastName"
                value={formData.lastName}
                brand={brand}
              />
            </div>

            <div
              className={`${Styles.date_picker_wrapper} flex flex-col relative mb-spacing-s`}
            >
              <TextFieldSts
                isDisabled
                label={dobPlaceholder ?? ""}
                data-testid="date-of-birth-organiser"
                id="date-of-birth-organiser"
                aria-label="date-of-birth-organiser"
                width={isDesktop ? "207px" : "170px"}
                name="dateofBirth"
                value={formatDate(formData.dateOfBirth)}
                brand={brand}
              />

              <div
                className="md:ml-spacing-s text-xs leading-[27px] font-semibold text-text-disabled 
                  absolute left-[214px] top-[16px]"
              >
                <p>Age: {calculateAge()}</p>
              </div>
            </div>

            {renewalInfoText && (
              <div className="mt-spacing-s  w-full">
                <InlineInfoBox
                  tabIndex={-1}
                  className={`px-spacing-m py-spacing-xs w-full font-body-font font-normal
                          text-2xs text-text-functional-1 rounded-lg border !border-border-select
                          mt-spacing-s mb-0 mx-auto leading-spacing-l bg-surface-info-2 ${Styles.infoBox}`}
                  content={
                    <RichText
                      richText={renewalInfoText}
                      onClick={handleWebChatClick}
                      className="[&_a]:!break-keep"
                    />
                  }
                />
              </div>
            )}

            <div className={` ${Styles.address_block}`}>
              {showPostcodeSearch && (
                <div className="md:text-xs text-left text-2xs  relative">
                  <div className="postcode-wrapper font-body-font mt-spacing-s">
                    <TextFieldSts
                      id="scrollableInput"
                      type="text"
                      name="address"
                      data-testid="address-organiser"
                      width="100%"
                      autoComplete="off"
                      state={addressErrors ? "error" : ""}
                      message={addressErrors || ""}
                      placeholder={props?.adressInputPlaceholder}
                      value={postCode ?? ""}
                      onChange={(e) => {
                        if (e.trim().length > 2) {
                          if (e.trim().length === 3) {
                            setSuggestions([]);
                          }
                          setValueAddress(e.trim());
                        }
                        scrollInputToTop();
                      }}
                      label="address"
                      labelLeftPadding="16px"
                      brand={brand}
                    />
                  </div>
                  {suggestions.length > 0 && (
                    <ul
                      className={`${
                        Styles.scroll
                      } absolute z-10 bg-white w-full border-gray-300 rounded-lg mt-1 ${
                        suggestions.length > 5
                          ? "overflow-y-scroll md:max-h-[215px] sm:max-h-[200px]"
                          : ""
                      }`}
                    >
                      {suggestions.map((suggestion) => (
                        <li
                          key={suggestion.id}
                          className="cursor-pointer p-2 hover:bg-gray-100"
                        >
                          <button
                            id={`suggestion-button-${suggestion.id}`}
                            type="button"
                            className="w-full h-full text-left "
                            onClick={() => {
                              handleAddressSelection(suggestion);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                handleAddressSelection(suggestion);
                              }
                            }}
                          >
                            {`${suggestion.attributes.address}, ${suggestion.attributes.description}`}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className=" flex mt-spacing-xs">
                    <Button
                      id="enterManuallyButton"
                      btnType="label"
                      type="button"
                      label={enterAddressButton ?? ""}
                      className="text-interactive-secondary-default text-2xs font-semibold font-['Proxima Nova'] underline leading-normal"
                      onClick={() => {
                        setShowPostcodeSearch(false);
                        setSuggestions([]);
                        setInvalidErrorMessage({});
                      }}
                      isOnlyActiveOnPress
                    />
                  </div>
                </div>
              )}

              {!showPostcodeSearch && (
                <div className=" md:text-xs text-left text-2xs my-spacing-l">
                  <TransformAnimation
                    currentPage={props.usagePage}
                    isSubSectionFilled={!!formData.firstLineOfAddress}
                  >
                    <div className={` ${Styles.address_field} mt-spacing-s`}>
                      <TextFieldSts
                        label={addressLineOne}
                        name="line1"
                        id="addressLineOne"
                        width="100%"
                        state={invalidErrorMessage.line1 ? "error" : ""}
                        message={invalidErrorMessage.line1 || ""}
                        onChange={(e) => handleInputChange("line1", e)}
                        value={formData.firstLineOfAddress}
                        maxLength={40}
                        labelLeftPadding="16px"
                        brand={brand}
                      />
                    </div>
                    <div className={` ${Styles.address_field} mt-spacing-s`}>
                      <TextFieldSts
                        label={addressLineTwo}
                        name="line2"
                        id="addressLineTwo"
                        width="100%"
                        state={invalidErrorMessage?.line2 ? "error" : ""}
                        message={invalidErrorMessage?.line2 || ""}
                        onChange={(e) => handleInputChange("line2", e)}
                        value={formData.secondLineOfAddress}
                        maxLength={40}
                        labelLeftPadding="16px"
                        brand={brand}
                      />
                    </div>
                    <div className={` ${Styles.address_field} mt-spacing-s`}>
                      <TextFieldSts
                        label={townCityPlaceholder}
                        name="city"
                        id="selectCity"
                        width="100%"
                        autoComplete="off"
                        state={invalidErrorMessage.city ? "error" : ""}
                        message={invalidErrorMessage.city || ""}
                        onChange={(e) => handleInputChange("city", e)}
                        value={formData.city}
                        maxLength={40}
                        labelLeftPadding="16px"
                        brand={brand}
                      />
                    </div>
                    <div
                      className="mt-spacing-s"
                      data-testid="postcode-wrapper"
                    >
                      <TextFieldSts
                        label={postcodePlaceholder}
                        name="postalCode"
                        id="postcodePlaceholder"
                        width="100%"
                        state={invalidErrorMessage.postalCode ? "error" : ""}
                        message={invalidErrorMessage.postalCode || ""}
                        onChange={(e) => handleInputChange("postalCode", e)}
                        value={formData.postCode}
                        labelLeftPadding="16px"
                        brand={brand}
                      />
                    </div>
                    <div className=" flex mt-spacing-xs">
                      <Button
                        id="searchByPostCodeButton"
                        btnType="label"
                        type="button"
                        label={searchByPostCodeButton ?? ""}
                        className={`${getTextColorClasses(
                          brand
                        )} text-2xs font-semibold font-['Proxima Nova'] underline leading-normal`}
                        onClick={() => {
                          setShowPostcodeSearch(true);
                          setSuggestions([]);
                          setPostCode("");
                          setInvalidErrorMessage({});
                          setTimeout(() => {
                            document
                              .getElementById("scrollableInput-input")
                              ?.focus();
                          }, 100);
                        }}
                        isOnlyActiveOnPress
                      />
                    </div>
                  </TransformAnimation>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default withBrand(OrganizerEditForm);
